import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, MapPin, Briefcase, Users, Filter } from "lucide-react";
import client from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/Badges";

export default function Jobs() {
  const [params] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [countries, setCountries] = useState([]);
  const [search, setSearch] = useState(params.get("q") || "");
  const [country, setCountry] = useState("all");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/public/jobs", { params: { search, country } });
      setJobs(data);
    } finally {
      setLoading(false);
    }
  }, [search, country]);

  useEffect(() => {
    client.get("/public/countries").then((r) => setCountries(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <div className="mb-8">
        <h1 className="font-display text-4xl font-extrabold text-[#0A192F]">Open Positions</h1>
        <p className="text-slate-500 mt-2">Browse and apply to international opportunities across our global network.</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-3 mb-8">
        <div className="flex-1 flex items-center gap-2 px-3 bg-white rounded-xl border border-slate-200">
          <Search className="h-5 w-5 text-slate-400" />
          <input data-testid="jobs-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search position, title or skill..." className="flex-1 py-3 outline-none text-sm bg-transparent" />
        </div>
        <div className="flex items-center gap-2 px-3 bg-white rounded-xl border border-slate-200">
          <Filter className="h-4 w-4 text-slate-400" />
          <select data-testid="jobs-country-filter" value={country} onChange={(e) => setCountry(e.target.value)}
            className="py-3 pr-6 outline-none text-sm bg-transparent min-w-[180px]">
            <option value="all">All Countries</option>
            {countries.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="grid gap-4">{[1, 2, 3].map((i) => <div key={i} className="h-32 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <Briefcase className="h-10 w-10 mx-auto mb-3 opacity-40" />
          No jobs match your filters.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-5">
          {jobs.map((j) => (
            <div key={j.id} data-testid={`job-card-${j.id}`} className="hover-lift bg-white rounded-2xl border border-slate-200 p-6 cursor-pointer" onClick={() => nav(`/jobs/${j.id}`)}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display font-semibold text-lg text-[#0A192F]">{j.title}</h3>
                  <p className="text-sm text-slate-500 mt-0.5">{j.client_name}</p>
                </div>
                <StatusBadge value={j.status} />
              </div>
              <div className="flex flex-wrap gap-4 mt-4 text-sm text-slate-600">
                <span className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-[#0088FF]" /> {j.country}</span>
                <span className="flex items-center gap-1.5"><Briefcase className="h-4 w-4 text-[#0088FF]" /> {j.experience}</span>
                <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-[#0088FF]" /> {j.vacancies} vacancies</span>
              </div>
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
                <span className="font-semibold text-[#0A192F]">{j.salary}</span>
                <Button size="sm" data-testid={`job-view-${j.id}`} className="bg-[#0088FF] hover:bg-[#0070E0]">View &amp; Apply</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
